import React from "react";
import {
  Datagrid,
  TextField,
  Filter,
  TextInput,
  CreateButton,
  Record,
  FunctionField,
  Responsive,
  SimpleList,
} from "react-admin";
import "../../common/styles/list-style.css";
import { IFleetManagerGridView } from "../../../common/data/data-types/fleet-managers";
import IndeterminateCheckBoxIcon from "@material-ui/icons/IndeterminateCheckBox";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import { Box, useTheme, Theme, Tooltip } from "@material-ui/core";
import { useSelector } from "react-redux";
import { AppState } from "../../../common/types";
import { useListContext, useTranslate } from "react-admin";
import { ActionsField } from "../../common/data-grid-fields/ActionsField";
import { ArrayField } from "../../common/data-grid-fields/ArrayField";
import { IResourceListProps } from "../../common/ra-types";
import { useSearchFields } from "../../../common/hooks/useSearchFields";
import { ProLogsEmpty } from "../../common/list/ProLogsEmpty";
import SupervisorAccountOutlinedIcon from "@material-ui/icons/SupervisorAccountOutlined";
import { DeleteRecordMenuItem } from "../../common/data-grid-fields/DeleteRecordMenuItem";
import permissionNames from "../../../common/auth/access-control/permissionNames";
import { useActionPermissions } from "../../../common/auth/useActionPermissions";
import { useFieldPermissions } from "../../../common/hooks/useFieldPermissions";
import { protectedEntities } from "../../../common/auth/permissions";
import { FieldPermissionsContext } from "../../common/field-permissions/FieldPermissionsContext";
import { ProtectedElement } from "../../common/field-permissions/ProtectedElement";
import { DefaultProLogsList } from "../../common/list/DefaultProLogsList";
import { useResetListSearchField } from "../../../common/hooks/useResetListSearchField";
import PersonIcon from "@material-ui/icons/Person";
import EmailIcon from "@material-ui/icons/Email";
import AssignmentIndIcon from "@material-ui/icons/AssignmentInd";
import GroupIcon from "@material-ui/icons/Group";
import { ResetFleetManagerPasswordMenuItem } from "./ResetFleetManagerPasswordMenuItem";
import { VpnKey } from "@material-ui/icons";

const ListFilter = (props: any) => {
  const { loading } = useListContext();
  useSearchFields(props, ["fleetManagerName", "email"]);
  useResetListSearchField(props);
  return (
    <Filter {...props}>
      {!loading && (
        <TextInput
          className="fleet-managers-list-filter search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
          autoFocus
          autoComplete="off"
        />
      )}
      {loading && (
        <TextInput
          className="fleet-managers-list-filter search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
        />
      )}
    </Filter>
  );
};

export const FleetManagersList = (props: IResourceListProps) => {
  const themeName = useSelector((state: AppState) => state.theme);
  const theme: Theme = useTheme();
  const translate = useTranslate();
  const permissions = useActionPermissions();
  const fieldPermissions = useFieldPermissions(
    protectedEntities.FleetManager,
    (x) => x.list
  );

  const ListActions = () => {
    const { basePath } = useListContext();
    const permissions = useActionPermissions();
    return (
      <>
        {permissions &&
          permissions[permissionNames.admin.fleetManagers.main] &&
          permissions[permissionNames.admin.fleetManagers.create] && (
            <CreateButton basePath={basePath} />
          )}
      </>
    );
  };

  interface SimpleListProps {
    record?: Record;
  }

  const RenderPrimaryTextForSimpleList = ({ record }: SimpleListProps) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          gap: "10px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <Tooltip title="Fleet manager name">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PersonIcon
              style={{
                color: theme.palette.success.main,
              }}
            />
            <ProtectedElement source="fleetManagerName">
              <TextField
                source="fleetManagerName"
                label="resources.admin-fleet-managers.labels.fullName"
              />
            </ProtectedElement>
          </div>
        </Tooltip>
        <FunctionField<IFleetManagerGridView & Record>
          source="status"
          headerClassName="centered-content"
          label="resources.common.labels.active"
          render={(record) => (
            <Box className="centered-content">
              {record?.status === "Active" ? (
                <CheckBoxIcon
                  className={record.status}
                  style={{
                    color:
                      themeName === "dark"
                        ? theme.palette.success.dark
                        : theme.palette.success.light,
                  }}
                />
              ) : (
                <IndeterminateCheckBoxIcon
                  className={record?.status}
                  style={{
                    color:
                      themeName === "dark"
                        ? theme.palette.error.dark
                        : theme.palette.error.light,
                  }}
                />
              )}
            </Box>
          )}
        />
      </div>
    );
  };

  const RenderSecondaryTextForSimpleList = ({ record }: SimpleListProps) => {
    return (
      <div
        style={{
          marginTop: "14px",
          display: "flex",
          flexDirection: "column",
          gap: "6px",
        }}
      >
        <Tooltip title="Email">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <EmailIcon
              style={{
                color: theme.palette.success.main,
              }}
            />
            <ProtectedElement source="email">
              <TextField
                style={{ color: theme.palette.text.secondary }}
                source="email"
                label="resources.common.labels.email"
              />
            </ProtectedElement>
          </div>
        </Tooltip>
        <Tooltip title="Role">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <AssignmentIndIcon
              style={{
                color: theme.palette.success.main,
              }}
            />
            <ProtectedElement source="role">
              <TextField
                style={{ color: theme.palette.text.secondary }}
                source="role"
                label="resources.admin-fleet-managers.labels.role"
              />
            </ProtectedElement>
          </div>
        </Tooltip>
        <Tooltip title="Groups">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
              color: theme.palette.text.secondary,
            }}
          >
            <GroupIcon
              style={{
                color: theme.palette.success.main,
              }}
            />
            <ProtectedElement source="groups">
              <ArrayField
                record={record}
                source="groups"
                label="resources.admin-fleet-managers.labels.groups"
                sortable={false}
                displayedElementsCount={1}
                emptyArrayDisplay={translate(
                  "resources.admin-fleet-managers.labels.allGroups"
                )}
              />
            </ProtectedElement>
          </div>
        </Tooltip>
      </div>
    );
  };

  return (
    <div className="content-container">
      <FieldPermissionsContext.Provider value={fieldPermissions}>
        <DefaultProLogsList
          empty={<ProLogsEmpty iconType={SupervisorAccountOutlinedIcon} />}
          className="content-list"
          title="resources.admin-fleet-managers.name"
          bulkActionButtons={false}
          actions={<ListActions />}
          filters={<ListFilter />}
          sort={{ field: "fleetManagerName", order: "ASC" }}
          {...props}
        >
          <Responsive
            small={
              <SimpleList
                className="simple-list-wrapper"
                linkType="edit"
                primaryText={(record) => (
                  <RenderPrimaryTextForSimpleList record={record} />
                )}
                secondaryText={(record) => (
                  <RenderSecondaryTextForSimpleList record={record} />
                )}
                rightIcon={(record) => {
                  return (
                    <ActionsField
                      hasShow={props.hasShow}
                      {...props}
                      record={record}
                      editable={() =>
                        permissions &&
                        permissions[permissionNames.admin.fleetManagers.main] &&
                        permissions[permissionNames.admin.fleetManagers.update]
                      }
                      canActivateOrDeactivate={() =>
                        permissions &&
                        permissions[permissionNames.admin.fleetManagers.main] &&
                        permissions[permissionNames.admin.fleetManagers.update]
                      }
                    >
                      {permissions &&
                        permissions[permissionNames.admin.fleetManagers.main] &&
                        permissions[
                          permissionNames.admin.fleetManagers.delete
                        ] && <DeleteRecordMenuItem />}
                    </ActionsField>
                  );
                }}
              />
            }
            medium={
              <Datagrid>
                <ProtectedElement source="fleetManagerName">
                  <TextField
                    source="fleetManagerName"
                    label="resources.admin-fleet-managers.labels.fullName"
                  />
                </ProtectedElement>
                <ProtectedElement source="email">
                  <TextField
                    source="email"
                    label="resources.common.labels.email"
                  />
                </ProtectedElement>
                <ProtectedElement source="role">
                  <TextField
                    source="role"
                    label="resources.admin-fleet-managers.labels.role"
                  />
                </ProtectedElement>
                <ProtectedElement source="groups">
                  <ArrayField
                    source="groups"
                    label="resources.admin-fleet-managers.labels.groups"
                    sortable={false}
                    displayedElementsCount={1}
                    emptyArrayDisplay={translate(
                      "resources.admin-fleet-managers.labels.allGroups"
                    )}
                  />
                </ProtectedElement>
                <FunctionField<IFleetManagerGridView & Record>
                  source="status"
                  headerClassName="centered-content"
                  label="resources.common.labels.active"
                  render={(record) => (
                    <Box className="centered-content">
                      {record?.status === "Active" ? (
                        <CheckBoxIcon
                          className={record.status}
                          style={{
                            color:
                              themeName === "dark"
                                ? theme.palette.success.dark
                                : theme.palette.success.light,
                          }}
                        />
                      ) : (
                        <IndeterminateCheckBoxIcon
                          className={record?.status}
                          style={{
                            color:
                              themeName === "dark"
                                ? theme.palette.error.dark
                                : theme.palette.error.light,
                          }}
                        />
                      )}
                    </Box>
                  )}
                />
                <ActionsField
                  hasShow={props.hasShow}
                  editable={() =>
                    permissions &&
                    permissions[permissionNames.admin.fleetManagers.main] &&
                    permissions[permissionNames.admin.fleetManagers.update]
                  }
                  canActivateOrDeactivate={() =>
                    permissions &&
                    permissions[permissionNames.admin.fleetManagers.main] &&
                    permissions[permissionNames.admin.fleetManagers.update]
                  }
                >
                  {permissions &&
                    permissions[permissionNames.admin.fleetManagers.main] &&
                    permissions[permissionNames.admin.fleetManagers.delete] && (
                      <DeleteRecordMenuItem />
                    )}
                  <ResetFleetManagerPasswordMenuItem
                    label="Reset password..."
                    icon={<VpnKey />}
                  />
                </ActionsField>
              </Datagrid>
            }
          />
        </DefaultProLogsList>
      </FieldPermissionsContext.Provider>
    </div>
  );
};
